// The Driver tab: status, then the document record, then the driver-side money and
// history cards that used to sit interleaved on the user page.
//
// It owns `useDriverRecord` and therefore owns the inspector too. That is deliberate:
// the tab stays mounted at ONE position in the tree whether the inspector is open or
// not, so opening a document does not unmount the hook and refetch the whole record.
// The shell above only decides whether to draw its own chrome — see `inspecting`.
import React from 'react';
import { Box, Stack, Typography, CircularProgress, Button } from '../../ui/core';
import { scrollY } from '../../ui/styles';
import { useHasPermission } from '../../helpers/api/auth/index';
import { PERMISSIONS } from '../../helpers/api/auth/permissions';
import useDriverRecord from '../DriverProfile/useDriverRecord';
import RecordPanel from '../DriverProfile/RecordPanel';
import Inspector from '../DriverProfile/Inspector';
import DriverStatusCard from './DriverStatusCard';
import { TabGrid } from './parts';

import DriverBalanceCard from '../../components/widgets/Users/DriverBalanceCard';
import DriverCashFlowCard from '../../components/widgets/Users/DriverCashFlowCard';
import DriverScoreCard from '../../components/widgets/Users/DriverScoreCard';
import DriveHistoryCard from '../../components/widgets/Users/DriveHistoryCard';
import DriverQuestsCard from '../../components/widgets/Users/DriverQuestsCard';
import DriverBlockDates from '../../components/widgets/Users/DriverBlockDates';
import FleetOperatorCard from '../../components/widgets/Users/FleetOperatorCard';
import DriverStatusHistoryCard from '../../components/widgets/Users/DriverStatusHistoryCard';
import UserReviewsCard from '../../components/widgets/Users/UserReviewsCard';

/**
 * @param onInspectingChange lets the shell hide its header and tabs while a document is
 *                           open. Reported up rather than derived from the URL in two
 *                           places, so there is one source of truth for "the inspector
 *                           has the page".
 */
const DriverTab = ({ user, userRawUnsafe, name, onInspectingChange }) => {
  // The user document already carries `driver_id`, so the id lookup the standalone
  // route has to do is skipped entirely here.
  const r = useDriverRecord({ routeId: user.driver_id, knownDriverId: user.driver_id });
  // Driver payments move real money, so the balance card stays admin-only exactly as it
  // was on the old page.
  const hasAdminPermission = useHasPermission('ADMIN');
  // MANAGE_DRIVERS remains the document permission: it decides scans, vehicles and
  // mutations which belong to the driver record itself.
  const canManageDrivers = useHasPermission(PERMISSIONS.MANAGE_DRIVERS);
  // VIEW_DRIVER owns the read-only operating record: money, drives, ratings, reviews,
  // standing history and blocked dates. A manager also sees those sections even if
  // their permissions array predates VIEW_DRIVER.
  const canViewDrivers = useHasPermission(PERMISSIONS.VIEW_DRIVER);
  const canViewDriverOperations = canManageDrivers || canViewDrivers;

  // Guarded, not just unrendered: `r.selected` comes off the URL, so without this a
  // pasted `?doc=…` would open a scan for someone who may not see the record at all.
  const inspecting = canManageDrivers && Boolean(r.selected);
  React.useEffect(() => {
    onInspectingChange(inspecting);
    // Told again on unmount, or leaving the tab mid-review would leave the shell
    // convinced it should still be hiding its own header.
    return () => onInspectingChange(false);
  }, [inspecting, onInspectingChange]);

  if (r.loading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress size={22} />
      </Stack>
    );
  }

  if (r.error) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
        <Typography sx={{ fontSize: '14px' }}>{r.error}</Typography>
        <Button variant="outlined" onClick={r.reload} sx={{ textTransform: 'none' }}>
          Try again
        </Button>
      </Stack>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <input ref={r.fileInput} type="file" accept="image/jpeg" hidden onChange={(e) => r.upload(e.target.files?.[0])} />

      {inspecting ? (
        // The whole page, tab bar and header included. The scan needs the width and the
        // record does not need to stay on screen while it is read — measured in
        // DriverProfilePrototype/NOTES.md, and this page stacks more chrome than the
        // standalone profile did.
        <Inspector
          profile={r.profile}
          requirement={r.selected}
          driverName={name}
          photoUrl={user.imageUrl}
          onBack={r.closeItem}
          viewingId={r.viewingId}
          onViewVersion={(rowId) => r.setSearch({ v: rowId }, { replace: true })}
          onStep={r.step}
          stepIndex={r.stepIndex}
          stepCount={r.stepCount}
          onDecide={r.decide}
          onUpload={r.pickFile}
          extraction={r.extraction}
          extractionLoading={r.extractionLoading}
          extractionError={r.extractionError}
          fieldRows={r.fieldRows}
          onSaveFields={r.saveFields}
          onReplay={r.replay}
          busy={r.busy}
        />
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, ...scrollY }}>
          {/* 1240, matching Account and Rider: two columns need two measures, and the
              header sits on the same width so it is not hard left while the cards are
              centred. */}
          <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, sm: 3 }, py: 2.5 }}>
            <TabGrid
              // The record — status, then the requirements it rolls up, then the vehicles
              // that group those requirements — stays full width and vertical because it
              // is ONE thing read in one order. Split across columns, a requirement ends
              // up beside the status it feeds instead of under it.
              full={
                <>
                  <DriverStatusCard
                    profile={r.profile}
                    user={user}
                    onApplicationAction={r.actOnApplication}
                    onStandingAction={r.actOnStanding}
                    onStandingReevaluate={r.reevaluateStanding}
                    busy={r.busy}
                  />
                  {canManageDrivers && (
                    <RecordPanel
                      profile={r.profile}
                      driver={r.driver}
                      mode={r.mode}
                      onModeChange={(next) => r.setSearch({ mode: next === 'all' ? null : next }, { replace: true })}
                      onSelect={r.select}
                      screening={r.screening}
                      screeningError={r.screeningError}
                      events={r.events}
                      busy={r.busy}
                      onAddVehicle={r.addVehicle}
                      onRemoveVehicle={r.removeVehicle}
                      onGrandfatherVehicle={r.setVehicleGrandfathered}
                    />
                  )}
                </>
              }
              // Everything below the record is independent of it, so it is the grid:
              // operations on the left, reputation and standing on the right. These are
              // the read-only operating sections VIEW_DRIVER owns; document management
              // and fleet membership remain MANAGE_DRIVERS territory.
              left={
                canViewDriverOperations && (
                  <>
                    {hasAdminPermission && <DriverBalanceCard user={user} />}
                    <DriverCashFlowCard user={user} />
                    <DriveHistoryCard user={user} />
                    <DriverQuestsCard user={user} />
                    <DriverBlockDates user={user} />
                  </>
                )
              }
              right={
                canViewDriverOperations && (
                  <>
                    <DriverScoreCard user={user} />
                    {/* Directly under Ratings, because it is the rows that average is made
                        of — the four metrics answer "is this driver good", these answer
                        "what happened". */}
                    <UserReviewsCard user={user} direction="driver" />
                    {canManageDrivers && <FleetOperatorCard user={user} />}
                    {/* Overlaps the event log in the record above, which already lists every
                        `driverApplicationStatusUpdates` row interleaved with the document
                        events. Kept anyway for one thing the log does NOT have: the
                        suspension count. The right end state is that count in the event
                        log's header, and this section gone. */}
                    <DriverStatusHistoryCard userRawUnsafe={userRawUnsafe} />
                  </>
                )
              }
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DriverTab;
